import React, { useEffect, useState } from 'react';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../TwAutocomplete/TwAutocomplete';
import { SxProps } from '@mui/material';
import { useLazyGetTripsByOrderIdQuery, useLazyGetTripsQuery } from '../../redux/api/trips/tripApi';
import { TripByOrderIdResponse, TripSearchResponse } from '../../redux/api/trips/trips.model';

type TripSelectorProps = {
  label?: string;
  selectedTrip: TwAutocompleteOption | null | undefined;
  hasError: boolean;
  onChange: (value: string) => void;
  setSelectedTrip: (trip: TwAutocompleteOption | null) => void;
  sx?: SxProps;
  readOnly?: boolean;
  enableLtl?: boolean;
  disabled?: boolean;
  searchByOrderId?: boolean;
  orderId?: string;
};

const TwTripSelector: React.FC<TripSelectorProps> = ({
  label,
  selectedTrip,
  hasError,
  setSelectedTrip,
  onChange,
  sx = {},
  orderId,
  searchByOrderId = false,
  readOnly = false,
  enableLtl = false,
  disabled = false
}): JSX.Element => {
  const [tripOptions, setTripOptions] = React.useState<TwAutocompleteOption[]>([]);
  const [searchTrip, setSearchTrip] = useState<string>('');
  const [blockFetchTrips, setBlockFetchTrips] = useState<boolean>(false);

  const [getTripsByOrderId, { data: tripListByOrderId = [] }] = useLazyGetTripsByOrderIdQuery();
  const [getTrips, { data: tripList = { content: [], total: 0 } }] = useLazyGetTripsQuery();

  useEffect(() => {
    if (blockFetchTrips || readOnly) return;

    if (!searchByOrderId || !orderId) {
      getTrips({
        params: {
          search: searchTrip,
          offset: 0,
          limit: 5
        }
      });

      return;
    }

    getTripsByOrderId({ orderId });
  }, [searchByOrderId, orderId, blockFetchTrips, readOnly]);

  useEffect(() => {
    if (searchByOrderId) return;

    if (tripList.content.length > 0) {
      setTripOptions(transformTripList(tripList.content));
    }
  }, [JSON.stringify(tripList.content), searchByOrderId]);

  useEffect(() => {
    if (!searchByOrderId) return;

    if (tripListByOrderId.length > 0) {
      setTripOptions(transformTripListByOrderId(tripListByOrderId));
    }
  }, [JSON.stringify(tripListByOrderId), searchByOrderId]);

  // Transform trip list
  const transformTripList = (trips: TripSearchResponse[]): TwAutocompleteOption[] => {
    return trips.map((trip) => ({
      id: trip.id,
      value: trip.id,
      label: !enableLtl
        ? `${trip.order_readable_id}-${trip.trip_sort_order + 1}`
        : String(trip.readable_id)
    }));
  };

  // Transform trip list by order id
  const transformTripListByOrderId = (trips: TripByOrderIdResponse[]): TwAutocompleteOption[] => {
    return trips.map((trip) => ({
      id: trip.id,
      value: trip.id,
      label: trip.readable_id
    }));
  };

  const handleFilterOptions = (options: any): any => {
    if (searchByOrderId) {
      return options;
    }

    return options.filter((option: any) => option.label.includes(searchTrip));
  };

  return (
    <TwAutocomplete
      label={label}
      name="trip"
      filterOptions={handleFilterOptions}
      renderOption={(props, option): React.ReactNode => {
        return <RenderOption key={option.id} props={props} option={option} />;
      }}
      optionList={tripOptions}
      value={selectedTrip || null}
      onChange={(_, newValue: TwAutocompleteOption | null): void => {
        const selection = newValue ? ({ ...newValue } as TwAutocompleteOption) : null;
        setSelectedTrip(selection);
        onChange(newValue?.id || '');
      }}
      onInputChange={(_, newInputValue): void => {
        setSearchTrip(newInputValue || '');
        setBlockFetchTrips(false);
      }}
      error={hasError}
      sx={sx}
      readonly={readOnly}
      showEndAdornment={!readOnly}
      disabled={readOnly ? false : disabled}
    />
  );
};

export default TwTripSelector;
